// AddressAutocompleteInput.jsx
import React, { useEffect, useRef, useState } from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import useDebounce from 'hooks/useDebounce';
import { useAuth } from 'auth/Auth';
import { outlinedFieldProps } from '../utils/fieldStyles';

const MIN_CHARS = 3;

const AddressAutocompleteInput = ({
                                      id,
                                      label,
                                      placeholder = 'Start typing an address...',
                                      value,
                                      onChange,
                                      error = false,
                                      errorMessage = '',
                                      required = false,
                                      fullWidth = true,
                                      compact = false,
                                  }) => {
    const { axiosInstance } = useAuth();
    const [inputValue, setInputValue] = useState(value || '');
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [isFocused, setIsFocused] = useState(false);

    const sessionTokenRef = useRef(crypto.randomUUID());
    const justSelectedRef = useRef(false);

    const debouncedInput = useDebounce(inputValue, 300);

    useEffect(() => {
        setInputValue(value || '');
    }, [value]);

    useEffect(() => {
        if (justSelectedRef.current) {
            justSelectedRef.current = false;
            return;
        }
        if (!debouncedInput || debouncedInput.trim().length < MIN_CHARS) {
            setOptions([]);
            setHasSearched(false);
            return;
        }

        let cancelled = false;
        setLoading(true);

        axiosInstance
            .get('/external-api/address/autocomplete', {
                params: {
                    input: debouncedInput,
                    session_token: sessionTokenRef.current,
                },
            })
            .then((res) => {
                if (!cancelled) setOptions(res.data.predictions || []);
            })
            .catch(() => {
                if (!cancelled) setOptions([]);
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                    setHasSearched(true);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [debouncedInput]);

    const handleSelect = async (prediction) => {
        if (!prediction || typeof prediction === 'string') return;

        justSelectedRef.current = true;
        setOptions([]);

        // Use exactly what the user clicked, so the stored value matches the dropdown.
        const selectedText = prediction.description;
        setInputValue(selectedText);
        onChange(selectedText);

        // Still call details so the session token is consumed. Ignore the formatted address.
        try {
            await axiosInstance.get('/external-api/address/details', {
                params: {
                    place_id: prediction.place_id,
                    session_token: sessionTokenRef.current,
                },
            });
        } catch (err) {
            // Non-fatal: the selected text is already applied.
        } finally {
            sessionTokenRef.current = crypto.randomUUID();
            setHasSearched(false);
        }
    };

    const handleBlur = (e) => {
        setIsFocused(false);
        setOptions([]);
        setHasSearched(false);
        const trimmed = e.target.value.trim();
        setInputValue(trimmed);
        onChange(trimmed);
    };

    const shouldShowPopper = isFocused && (loading || options.length > 0);

    // Same priority as before, just no longer fed into MUI's layout-affecting helperText.
    const getHelperText = () => {
        if (error) return errorMessage || 'This field is required';
        if (loading) return 'Searching...';
        if (hasSearched && options.length === 0 && inputValue.trim().length >= MIN_CHARS) {
            return 'No matching address found.';
        }
        return '';
    };

    const helperText = getHelperText();

    return (
        // Reserves a fixed-height slot below the field for helper/error text,
        // so it toggling in/out never changes this box's height and never
        // pushes sibling fields around ("layout shift" / the disco effect).
        <Box sx={{ position: 'relative', pb: '1px' }}>
            <Autocomplete
                freeSolo
                fullWidth={fullWidth}
                open={shouldShowPopper}
                filterOptions={(x) => x}
                options={options}
                loading={loading}
                getOptionLabel={(option) =>
                    typeof option === 'string' ? option : option.description || ''
                }
                inputValue={inputValue}
                onInputChange={(_, newInputValue, reason) => {
                    setInputValue(newInputValue);
                    if (reason === 'input') {
                        justSelectedRef.current = false;
                    }
                }}
                onChange={(_, selected) => handleSelect(selected)}
                onFocus={() => setIsFocused(true)}
                onBlur={handleBlur}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        id={id}
                        {...outlinedFieldProps}
                        size={compact ? 'small' : outlinedFieldProps?.size}
                        label={compact ? undefined : (required ? `${label} *` : label)}
                        placeholder={placeholder}
                        error={error}
                        // No helperText prop here anymore — rendered separately below.
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {loading ? <CircularProgress color="inherit" size={16} /> : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                    />
                )}
            />
            {helperText && !error && (
                <Box
                    sx={{
                        position: 'absolute',
                        left: compact ? 0 : 14, // MUI's default helperText left padding when not compact
                        right: 0,
                        top: '100%',
                        mt: '3px',
                        fontSize: '0.75rem',
                        lineHeight: 1.2,
                        color: error ? 'error.main' : 'text.secondary',
                    }}
                >
                    {helperText}
                </Box>
            )}
        </Box>
    );
};

export default AddressAutocompleteInput;